import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AsyncPaginate,
  ComponentProps,
  LoadOptions,
  UseAsyncPaginateParams,
  withAsyncPaginate,
} from "react-select-async-paginate";
import { DefaultOption, SelectProps } from "./types";
import { customStyles } from "./styles";
import styles from "./Select.module.scss";
import { GroupBase, StylesConfig } from "react-select";
import { CreatableProps } from "react-select/creatable";
import Creatable from "react-select/creatable";
import { useRequests } from "Shared/Hook/requests";
import { ASYNC_LIMIT_PAGE } from "Shared/Constants/app";
import { useLocation } from "react-router";

type ApiResponseLike<T> =
  | T[]
  | {
    results?: T[];
    data?: { results?: T[] };
    items?: T[];
  }
  | any;

const AsyncSelect = <T = DefaultOption,>(props: SelectProps<T>) => {
  const {
    className = "",
    isCreatable,
    optionValue = "id",
    optionLabel = "name",
    asyncLoadOptionType,
    value,
    onChange,
    onCreateOption,
    editOptionLabel,
    editOptionValue,
    defaultOptions = [],
  } = props;

  const { dispatchOptions } = useRequests({
    asyncLoadOptionType,
  });

  const { pathname } = useLocation();

  const lastSearchRef = useRef<string>("");
  const offsetRef = useRef(0);
  const initialLoadRef = useRef(false);

  const requestSeqRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);

  const getLabel = (item: T): string => {
    if (typeof optionLabel === "function") return optionLabel(item);
    return String(item[optionLabel as keyof T] ?? "");
  };

  const getValue = (item: T): string => {
    return String(item[optionValue as keyof T] ?? "");
  };

  const createOption = (valueData: string, labelData: string): T => {
    if (typeof optionLabel === "function") {
      const obj: any = { [optionValue]: valueData };
      return obj as T;
    }
    return {
      [optionValue]: valueData,
      [optionLabel as string]: labelData,
    } as T;
  };

  const combinedOptions = useMemo(() => {
    if (!editOptionValue || !editOptionLabel) return [];
    return [createOption(editOptionValue, editOptionLabel)];
  }, [editOptionValue, editOptionLabel, optionValue, optionLabel]);

  useEffect(() => {
    if (!asyncLoadOptionType || !dispatchOptions) return;
    if (initialLoadRef.current) return;

    initialLoadRef.current = true;
    // `.unwrap()` rejects with a plain object; unhandled, it white-screens dev.
    dispatchOptions[asyncLoadOptionType as keyof typeof dispatchOptions]?.({
      limit: ASYNC_LIMIT_PAGE,
      offset: 0,
      search: "",
    })?.catch((error: unknown) => console.error("Error loading options:", error));

  }, [asyncLoadOptionType, dispatchOptions]);

  const extractResults = (resp: ApiResponseLike<T>): T[] => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp?.results)) return resp.results;
    if (Array.isArray(resp?.data?.results)) return resp.data.results;
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp?.items)) return resp.items;
    return [];
  };

  const loadOptions: LoadOptions<T, GroupBase<T>, unknown> = async (
    search: string
  ) => {
    if (!dispatchOptions || !asyncLoadOptionType) {
      return {
        options: editOptionValue ? (combinedOptions as T[]) : [],
        hasMore: false,
      };
    }

    if (search !== lastSearchRef.current) {
      offsetRef.current = 0;
      lastSearchRef.current = search;
    }
    
    const seq = ++requestSeqRef.current;

    try {
      setIsLoading(true);

      const resp = await dispatchOptions[
        asyncLoadOptionType as keyof typeof dispatchOptions
      ]({
        search: search || undefined,
        limit: ASYNC_LIMIT_PAGE,
        offset: offsetRef.current,
      });

      if (seq !== requestSeqRef.current) {
        return { options: [], hasMore: false };
      }

      const results = extractResults(resp);

      if (!results.length) {
        return {
          options: editOptionValue ? (combinedOptions as T[]) : [],
          hasMore: false,
        };
      }

      const apiOptions = results.filter((option) => {
        const optionId = String(getValue(option));
        const editId = String(editOptionValue ?? "");
        return !editId || optionId !== editId;
      });

      const hasMore = results.length === ASYNC_LIMIT_PAGE;
      if (hasMore) offsetRef.current += ASYNC_LIMIT_PAGE;

      return { options: apiOptions, hasMore };
    } catch (error) {
      console.error("Error loading options:", error);
      return {
        options: editOptionValue ? (combinedOptions as T[]) : [],
        hasMore: false,
      };
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      initialLoadRef.current = false;
      offsetRef.current = 0;
      lastSearchRef.current = "";
      requestSeqRef.current = 0;
    };
  }, []);

  const defaultValue = useMemo(() => {
    if (pathname.includes("add") || pathname.includes("create")) return null;

    if (value) return value as any;

    if (editOptionValue != null && editOptionLabel != null) {
      return createOption(editOptionValue, editOptionLabel) as any;
    }
    return null;
  }, [value, editOptionValue, editOptionLabel, optionValue, optionLabel, pathname]);

  type AsyncPaginateCreatableProps<
    OptionType,
    Group extends GroupBase<OptionType>,
    Additional,
    IsMulti extends boolean,
  > = CreatableProps<OptionType, IsMulti, Group> &
    UseAsyncPaginateParams<OptionType, Group, Additional> &
    ComponentProps<OptionType, Group, IsMulti>;

  type AsyncPaginateCreatableType = <
    OptionType,
    Group extends GroupBase<OptionType>,
    Additional,
    IsMulti extends boolean = false,
  >(
    props: AsyncPaginateCreatableProps<OptionType, Group, Additional, IsMulti>
  ) => ReactElement;

  const CreatableAsyncPaginate = useMemo(() => {
    return withAsyncPaginate(Creatable) as AsyncPaginateCreatableType;
  }, []);

  const GeneralAsyncSelect = isCreatable
    ? CreatableAsyncPaginate
    : (AsyncPaginate as AsyncPaginateCreatableType);

  const initialDefaultOptions = true;

  return (
    <GeneralAsyncSelect
      loadOptions={loadOptions}
      defaultOptions={initialDefaultOptions}
      styles={customStyles as StylesConfig<T>}
      classNamePrefix="react-select"
      defaultValue={defaultValue}
      value={value}
      onChange={onChange}
      hideSelectedOptions={false}
      isLoading={isLoading}
      getOptionLabel={(option: any) => (option ? getLabel(option) : "")}
      getOptionValue={(option: any) => (option ? getValue(option) : "")}
      isClearable
      onCreateOption={onCreateOption}
      isMulti={props?.isMulti}
      isDisabled={props.isDisabled}
      className={`${styles.select__input} ${className}`}
      getNewOptionData={(inputValue: any, inputLabel: any) => {
        return createOption(String(inputValue), String(inputLabel));
      }}
      debounceTimeout={300}
    />
  );
};

export default AsyncSelect;
